import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AssetModelMetadata } from '@evimo/types';
import {
  ASSET_OVERVIEW_SEARCH_DEBOUNCE_MS,
  toAssetOverviewSearchQuery,
} from '~/app/hooks/asset/asset-overview-search';
import { useDebouncedValue } from '~/app/hooks/useDebouncedValue';
import { AssetModelService } from '~/app/services/asset-model/asset-model.service';
import {
  AssetService,
  type AssetListItem,
  type AssetPaginationMeta,
} from '~/app/services/asset/asset.service';
import {
  AssetTypeService,
  type AssetTypeMetadata,
} from '~/app/services/asset-type/asset-type.service';
import { useAuth } from '~/app/auth/context/AuthContext';
import {
  assetListRefetchInterval,
  shouldRetryAssetList,
} from '~/app/components/asset-overview/utils';

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 10;

const emptyMeta: AssetPaginationMeta = {
  total: 0,
  page: DEFAULT_PAGE,
  limit: DEFAULT_LIMIT,
  totalPages: 0,
};

type TablePagination = {
  current?: number;
  pageSize?: number;
};

export function useAssetOverview() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState('');
  const [selectedAssetTypeId, setSelectedAssetTypeId] = useState('');
  const [selectedAssetModelId, setSelectedAssetModelId] = useState('');
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);

  const organizationId = user?.organizationId ?? '';

  const debouncedSearchValue = useDebouncedValue(
    searchValue,
    ASSET_OVERVIEW_SEARCH_DEBOUNCE_MS,
  );
  const searchQuery = toAssetOverviewSearchQuery(debouncedSearchValue);

  const assetTypesQuery = useQuery({
    queryKey: ['assets', 'overview', 'asset-types'],
    queryFn: () => AssetTypeService.findAll(),
  });

  const assetModelsQuery = useQuery({
    queryKey: ['assets', 'overview', 'asset-models', selectedAssetTypeId],
    queryFn: () => AssetModelService.findAllByAssetTypeId(selectedAssetTypeId),
    enabled: !!selectedAssetTypeId,
  });

  const query = useQuery({
    queryKey: [
      'assets',
      'overview',
      organizationId,
      page,
      limit,
      searchQuery,
      selectedAssetTypeId,
      selectedAssetModelId,
    ],
    queryFn: () =>
      AssetService.listByOrganization({
        organizationId,
        page,
        limit,
        q: searchQuery,
        assetTypeId: selectedAssetTypeId || undefined,
        assetModelId: selectedAssetModelId || undefined,
      }),
    enabled: !!organizationId,
    // Keeps charger connectivity fresh while the page is open, but stops on an expired
    // session instead of polling 401s forever (#1100).
    refetchInterval: (listQuery) =>
      assetListRefetchInterval(listQuery.state.error),
    retry: shouldRetryAssetList,
    // Badges may be hours old when the operator comes back to the tab: refetch on focus
    // (the global 60 s staleTime keeps quick tab switches free).
    refetchOnWindowFocus: true,
  });

  const assets = query.data?.data ?? [];
  const meta = query.data?.meta ?? emptyMeta;
  const assetTypes = assetTypesQuery.data ?? [];
  const assetModels = selectedAssetTypeId ? (assetModelsQuery.data ?? []) : [];
  const error =
    query.error ?? assetTypesQuery.error ?? assetModelsQuery.error ?? null;

  const pagination = useMemo(
    () => ({
      current: meta.page,
      pageSize: meta.limit,
      total: meta.total,
      showSizeChanger: true,
    }),
    [meta.limit, meta.page, meta.total],
  );

  const handleSearchChange = (nextValue: string): void => {
    setSearchValue(nextValue);
    setPage(DEFAULT_PAGE);
  };

  const handleAssetTypeChange = (nextAssetTypeId: string): void => {
    setSelectedAssetTypeId(nextAssetTypeId);
    setSelectedAssetModelId('');
    setPage(DEFAULT_PAGE);
  };

  const handleAssetModelChange = (nextAssetModelId: string): void => {
    setSelectedAssetModelId(nextAssetModelId);
    setPage(DEFAULT_PAGE);
  };

  const handleTableChange = (nextPagination: TablePagination): void => {
    const nextPageSize = nextPagination.pageSize ?? DEFAULT_LIMIT;
    if (nextPageSize !== limit) {
      setLimit(nextPageSize);
      setPage(DEFAULT_PAGE);
      return;
    }
    setPage(nextPagination.current ?? DEFAULT_PAGE);
  };

  const refetch = useCallback(async (): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: ['assets', 'overview'],
      refetchType: 'active',
    });
  }, [queryClient]);

  return {
    assets,
    assetModels,
    assetTypes,
    error,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isLoadingAssetModels: assetModelsQuery.isLoading,
    isLoadingAssetTypes: assetTypesQuery.isLoading,
    pagination,
    searchValue,
    selectedAssetModelId,
    selectedAssetTypeId,
    handleAssetModelChange,
    handleAssetTypeChange,
    handleSearchChange,
    handleTableChange,
    refetch,
  } satisfies {
    assets: AssetListItem[];
    assetModels: AssetModelMetadata[];
    assetTypes: AssetTypeMetadata[];
    error: Error | null;
    isLoading: boolean;
    isFetching: boolean;
    isLoadingAssetModels: boolean;
    isLoadingAssetTypes: boolean;
    pagination: {
      current: number;
      pageSize: number;
      total: number;
      showSizeChanger: boolean;
    };
    searchValue: string;
    selectedAssetModelId: string;
    selectedAssetTypeId: string;
    handleAssetModelChange: (nextAssetModelId: string) => void;
    handleAssetTypeChange: (nextAssetTypeId: string) => void;
    handleSearchChange: (nextValue: string) => void;
    handleTableChange: (nextPagination: TablePagination) => void;
    refetch: () => Promise<void>;
  };
}
